import ContentTypeConstants from '../../../../core/content-type-constants/content-type.constants';
import { deleteRequest, getFileRequest, getRequest, patchJsonRequest, postRequest, putRequest } from '../../../../core/utils/api';
import { type KeycloakContext } from '../../../authentication/keycloak/KeycloakProvider';
import {
  type CreateDisplayRequest,
  type DashboardOfficeDetail,
  type DashboardOverview,
  type DashboardReporting,
  type DefaultThreshold,
  type Display,
  type EmployeePresence,
  type EmployeePresenceDayRow,
  type EmployeePresenceDetail,
  type ReportingComparisonMode,
  type UpdateDisplayRequest,
} from '../models/dashboard.models';

export const getDashboardOverview = async (keycloak: KeycloakContext, date?: string): Promise<DashboardOverview | undefined> => {
  const url = date ? `dashboard/overview?date=${date}` : 'dashboard/overview';
  const apiResponse = await getRequest<DashboardOverview>(url, keycloak);
  return apiResponse.error ? undefined : apiResponse.json;
};

export const getDashboardOfficeDetail = async (
  officeId: string,
  keycloak: KeycloakContext,
  date?: string,
): Promise<DashboardOfficeDetail | undefined> => {
  const url = date ? `dashboard/office/${officeId}?date=${date}` : `dashboard/office/${officeId}`;
  const apiResponse = await getRequest<DashboardOfficeDetail>(url, keycloak);
  return apiResponse.error ? undefined : apiResponse.json;
};

export interface ReportingFilter {
  readonly officeId?: string;
  readonly companyId?: string;
  readonly officeIds?: string[];
  readonly compareOffices?: boolean;
}

const buildReportingFilterParams = (filter?: ReportingFilter): string => {
  if (!filter) {
    return '';
  }

  let params = '';
  if (filter.officeId) {
    params += `&officeId=${filter.officeId}`;
  }
  if (filter.companyId) {
    params += `&companyId=${filter.companyId}`;
  }
  if (filter.officeIds && filter.officeIds.length > 0) {
    params += `&officeIds=${filter.officeIds.join(',')}`;
  }
  if (filter.compareOffices) {
    params += '&compareOffices=true';
  }

  return params;
};

export interface ReportingResult {
  readonly reporting?: DashboardReporting;
  readonly status: number;
}

export const getDashboardReporting = async (
  startDate: string,
  endDate: string,
  comparison: ReportingComparisonMode,
  keycloak: KeycloakContext,
  filter?: ReportingFilter,
): Promise<ReportingResult> => {
  const url = `dashboard/reporting?startDate=${startDate}&endDate=${endDate}&comparison=${comparison}${buildReportingFilterParams(filter)}`;
  const apiResponse = await getRequest<DashboardReporting>(url, keycloak);
  return { reporting: apiResponse.error ? undefined : apiResponse.json, status: apiResponse.status };
};

export const getReportingCsvExport = async (
  startDate: string,
  endDate: string,
  keycloak: KeycloakContext,
  filter?: ReportingFilter,
): Promise<Blob | undefined> => {
  const url = `dashboard/reporting/export-csv?startDate=${startDate}&endDate=${endDate}${buildReportingFilterParams(filter)}`;
  const apiResponse = await getFileRequest(url, ContentTypeConstants.CSV, keycloak);
  const response = apiResponse as unknown as Response;
  if (!response.ok) {
    return undefined;
  }

  return response.blob();
};

export const getEmployeePresence = async (
  startDate: string,
  endDate: string,
  keycloak: KeycloakContext,
  filter?: ReportingFilter,
): Promise<EmployeePresence[] | undefined> => {
  const url = `dashboard/employee-presence?startDate=${startDate}&endDate=${endDate}${buildReportingFilterParams(filter)}`;
  const apiResponse = await getRequest<{ employees: EmployeePresence[] }>(url, keycloak);
  return apiResponse.error ? undefined : apiResponse.json?.employees;
};

export const getEmployeePresenceDays = async (
  officeUserId: string,
  startDate: string,
  endDate: string,
  keycloak: KeycloakContext,
  filter?: ReportingFilter,
): Promise<EmployeePresenceDetail | undefined> => {
  const range = `startDate=${startDate}&endDate=${endDate}`;
  const url = `dashboard/employee-presence/${officeUserId}/days?${range}${buildReportingFilterParams(filter)}`;
  const apiResponse = await getRequest<EmployeePresenceDetail>(url, keycloak);
  return apiResponse.error ? undefined : apiResponse.json;
};

/// <summary>The same rows the employee CSV contains, as JSON, so the PDF export can print them
/// without parsing a CSV. Fetched on demand when the user actually exports — this is one row per
/// employee per office per day and has no business loading with the tab.</summary>
export const getEmployeePresenceDayRows = async (
  startDate: string,
  endDate: string,
  keycloak: KeycloakContext,
  filter?: ReportingFilter,
): Promise<EmployeePresenceDayRow[] | undefined> => {
  const range = `startDate=${startDate}&endDate=${endDate}`;
  const url = `dashboard/employee-presence/days?${range}${buildReportingFilterParams(filter)}`;
  const apiResponse = await getRequest<{ days: EmployeePresenceDayRow[] }>(url, keycloak);
  return apiResponse.error ? undefined : apiResponse.json?.days;
};

/// <summary>Mirrors getReportingCsvExport's blob handling. Pass officeUserId to export one
/// employee (the drill-down modal's download) instead of everyone in scope.</summary>
export const getEmployeePresenceCsvExport = async (
  startDate: string,
  endDate: string,
  keycloak: KeycloakContext,
  filter?: ReportingFilter,
  officeUserId?: string,
): Promise<Blob | undefined> => {
  const employeeParam = officeUserId ? `&officeUserId=${officeUserId}` : '';
  const range = `startDate=${startDate}&endDate=${endDate}`;
  const url = `dashboard/employee-presence/export-csv?${range}${buildReportingFilterParams(filter)}${employeeParam}`;
  const apiResponse = await getFileRequest(url, ContentTypeConstants.CSV, keycloak);
  const response = apiResponse as unknown as Response;
  if (!response.ok) {
    return undefined;
  }

  return response.blob();
};

export const getDefaultThreshold = async (keycloak: KeycloakContext, companyId?: string): Promise<DefaultThreshold | undefined> => {
  const url = companyId ? `threshold/default?companyId=${companyId}` : 'threshold/default';
  const apiResponse = await getRequest<DefaultThreshold>(url, keycloak);
  return apiResponse.error ? undefined : apiResponse.json;
};

export const updateDefaultThreshold = async (
  minPercentage: number,
  maxPercentage: number,
  keycloak: KeycloakContext,
  companyId?: string,
): Promise<DefaultThreshold | undefined> => {
  const apiResponse = await putRequest<DefaultThreshold>('threshold/default', keycloak, { minPercentage, maxPercentage, companyId });
  return apiResponse.error ? undefined : apiResponse.json;
};

export const getDisplays = async (officeId: string, keycloak: KeycloakContext): Promise<Display[] | undefined> => {
  const apiResponse = await getRequest<Display[]>(`display?officeId=${officeId}`, keycloak);
  return apiResponse.error ? undefined : apiResponse.json;
};

export const createDisplay = async (request: CreateDisplayRequest, keycloak: KeycloakContext): Promise<Display | undefined> => {
  const apiResponse = await postRequest<Display>('display', keycloak, request);
  return apiResponse.error ? undefined : apiResponse.json;
};

export const updateDisplay = async (
  displayId: string,
  request: UpdateDisplayRequest,
  keycloak: KeycloakContext,
): Promise<Display | undefined> => {
  const apiResponse = await patchJsonRequest<Display>(`display/${displayId}`, keycloak, request);
  return apiResponse.error ? undefined : apiResponse.json;
};

export const revokeDisplay = async (displayId: string, keycloak: KeycloakContext): Promise<boolean> => {
  const apiResponse = await postRequest(`display/${displayId}/revoke`, keycloak, {});
  return !apiResponse.error;
};

export const deleteDisplay = async (displayId: string, keycloak: KeycloakContext): Promise<boolean> => {
  const apiResponse = await deleteRequest(`display/${displayId}`, keycloak);
  return !apiResponse.error;
};

// On success this returns the updated Display itself (status will be 'live'), not a PairingPoll —
// that shape is only returned by the anonymous screen-side pollPairing endpoint.
export const pairDisplay = async (pairingCode: string, displayId: string, keycloak: KeycloakContext): Promise<Display | undefined> => {
  const apiResponse = await postRequest<Display>('display/pair', keycloak, { pairingCode, displayId });
  return apiResponse.error ? undefined : apiResponse.json;
};
